/**
 * useAriaVoice — Web Speech API wrapper per Aria
 *
 * - Sintesi vocale 100% client-side (zero costi, zero API key)
 * - Sceglie automaticamente la migliore voce italiana disponibile sul device
 * - Cancella eventuale speech precedente prima di partire (no overlap)
 * - Espone: speak, stop, toggle, supported, enabled, speaking
 *
 * Usato dai widget Aria nelle modalità premium (Welcome / Full / Cinematic).
 */
import { useEffect, useRef, useState, useCallback } from "react";

// Ranking voci italiane (più alta = preferita)
const VOICE_PREFERENCE = [
    "Alice",        // macOS / iOS — premium femminile
    "Federica",     // macOS — alternativa
    "Luca",         // macOS — premium maschile
    "Elsa",         // Windows 10/11
    "Cosimo",       // Windows
    "Google italiano", // Chrome desktop
    "Microsoft Elsa Online (Natural) - Italian (Italy)",
    "Microsoft Isabella Online (Natural) - Italian (Italy)",
];

const VOICE_STORAGE_KEY = "aria.voice.enabled";

function pickItalianVoice(voices) {
    if (!voices || voices.length === 0) return null;
    const italian = voices.filter((v) => /(^|-)it(-|_|$)/i.test(v.lang) || /italian/i.test(v.name));
    if (italian.length === 0) return null;
    // Ordina per preferenza
    for (const pref of VOICE_PREFERENCE) {
        const found = italian.find((v) => v.name.includes(pref));
        if (found) return found;
    }
    // fallback: primo italiano
    return italian[0];
}

export function useAriaVoice() {
    const synthRef = useRef(null);
    const voiceRef = useRef(null);
    const [supported, setSupported] = useState(false);
    const [enabled, setEnabled] = useState(() => {
        if (typeof window === "undefined") return true;
        try {
            const stored = window.localStorage.getItem(VOICE_STORAGE_KEY);
            // Default ON: solo se l'utente ha esplicitamente disattivato (=="0"), allora OFF.
            return stored !== "0";
        } catch (_e) { return true; }
    });
    const [speaking, setSpeaking] = useState(false);

    // Init synth + carica voci (alcuni browser le caricano in async)
    useEffect(() => {
        // ElevenLabs backend è sempre disponibile → supported sempre true.
        // Web Speech API resta come fallback se l'utente è offline o il backend down.
        setSupported(true);
        if (typeof window === "undefined" || !window.speechSynthesis) return;
        synthRef.current = window.speechSynthesis;
        const loadVoices = () => {
            const voices = synthRef.current.getVoices();
            voiceRef.current = pickItalianVoice(voices);
        };
        loadVoices();
        synthRef.current.addEventListener("voiceschanged", loadVoices);
        return () => {
            try { synthRef.current?.removeEventListener?.("voiceschanged", loadVoices); } catch (_e1) { /* ignore */ }
            try { synthRef.current?.cancel?.(); } catch (_e2) { /* ignore */ }
        };
    }, []);

    // Persisti la preferenza
    useEffect(() => {
        try {
            window.localStorage.setItem(VOICE_STORAGE_KEY, enabled ? "1" : "0");
        } catch (_e) { /* ignore */ }
    }, [enabled]);

    const stop = useCallback(() => {
        try {
            synthRef.current?.cancel();
        } catch (_e) { /* ignore */ }
        setSpeaking(false);
    }, []);

    /**
     * prime() — sblocca SIA Web Speech che HTML5 Audio context dentro un gesture utente.
     * Su Safari/iOS, l'audio HTML5 dev'essere "warmato" con un blob play() durante il
     * click, altrimenti il successivo audio.play() (anche se è la voce ElevenLabs)
     * fallisce con NotAllowedError se nel frattempo il gesture è scaduto.
     */
    const prime = useCallback(() => {
        // 1. Web Speech warmup (per i browser che lo richiedono)
        const synth = synthRef.current;
        if (synth) {
            try {
                synth.resume?.();
                const warmup = new SpeechSynthesisUtterance(" ");
                warmup.volume = 0;
                warmup.rate = 10;
                warmup.lang = voiceRef.current?.lang || "it-IT";
                if (voiceRef.current) warmup.voice = voiceRef.current;
                synth.speak(warmup);
            } catch (_e) { /* ignore */ }
        }
        // 2. HTML5 Audio context warmup (critico per ElevenLabs blob su Safari/iOS)
        try {
            // Silent MP3 ~50ms — dev'essere riprodotto dentro il gesture per
            // sbloccare l'audio context anche per i successivi <audio> dinamici.
            const silentMp3 = "data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/80DEKwAAA0gAAAAAVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/80DEVgAAA0gAAAAAVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
            const silent = new Audio(silentMp3);
            silent.volume = 0;
            // .play() ritorna Promise — ignoriamo silently se reject
            silent.play().catch(() => { /* ignore */ });
        } catch (_e) { /* ignore */ }
    }, []);

    const speak = useCallback(async (text) => {
        if (!enabled || !text) return;
        // Pulisci testo
        const clean = (text || "")
            .replace(/\*\*/g, "")
            .replace(/^▢\s*/gm, " ")
            .replace(/^[#>*-]\s*/gm, "")
            .replace(/\[(CTA|LEAD|ARIA_FOR_SITE)[^\]]*\]/g, "")
            .replace(/\s+/g, " ")
            .trim();
        if (!clean) return;

        // === ELEVENLABS BACKEND (voce naturale Matilda) ===
        // Nessun fallback su Web Speech API: se ElevenLabs fallisce restiamo
        // in silenzio invece di degradare a voce robotica del browser
        // (causa principale del bug "voce robotica al primo messaggio").
        try {
            // Stop tutto quello che sta suonando
            try { synthRef.current?.cancel(); } catch (_e) { /* ignore */ }
            setSpeaking(true);
            const apiBase = "" || "";
            const res = await fetch(`${apiBase}/api/aria/tts/speak`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text: clean }),
            });
            if (!res.ok) {
                setSpeaking(false);
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
            audio.onerror = () => { setSpeaking(false); URL.revokeObjectURL(url); };
            try {
                await audio.play();
            } catch (_playErr) {
                // NotAllowedError o simili: NON fare fallback robotic, resta in silenzio.
                // Il gesture utente è scaduto — accadrà solo se prime() non è stato chiamato.
                setSpeaking(false);
                URL.revokeObjectURL(url);
            }
        } catch (_e) {
            // Errore di rete/parse: silenzio
            setSpeaking(false);
        }
    }, [enabled]);

    const toggle = useCallback(() => {
        setEnabled((p) => {
            const next = !p;
            if (!next) {
                try { synthRef.current?.cancel(); } catch (_e) { /* ignore */ }
                setSpeaking(false);
            }
            return next;
        });
    }, []);

    return { supported, enabled, speaking, speak, stop, toggle, prime };
}
